import { ArrowRight24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/ds-react-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { useFormContext } from "react-hook-form";
import { DEFAULT_TIME_FORMAT, getInitialTimes } from "./utils";
import { Typography } from "@kaltura/ds-react-components";
import PickersUnit from "./pickers-unit/PickersUnit";
import { useContext } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";

export const StyledPickersWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        flexDirection: "column",
    },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.danger.main,
    marginTop: theme.spacing(1),
}));

const baseClassName = "kms-ds-create-room-modal-form-pickers";

interface PickersSectionProps {
    /**
     * Optional field name prefix for form field namespacing
     */
    fieldPrefix?: string;

    /**
     * if true, inputs are disabled
     */
    disabled?: boolean;

    /**
     * if true, skips the 24-hour max duration validation
     * @default false
     */
    skipMaxDurationValidation?: boolean;

    /**
     * ID of the element that describes this section for accessibility
     */
    "aria-describedby"?: string;
}

const PickersSection = ({
    fieldPrefix = "",
    disabled = false,
    skipMaxDurationValidation = false,
    "aria-describedby": ariaDescribedBy,
}: PickersSectionProps) => {
    const configContext = useContext(ConfigContext);
    const dateFormat = configContext.application.shortDateFormat || "DD/MM/YYYY";

    // Create field names with optional prefix
    const roomStartTimeDateName = fieldPrefix ? `${fieldPrefix}.startTimeDate` : "startTimeDate";
    const roomStartTimeTimeName = fieldPrefix ? `${fieldPrefix}.startTimeTime` : "startTimeTime";
    const roomEndTimeDateName = fieldPrefix ? `${fieldPrefix}.endTimeDate` : "endTimeDate";
    const roomEndTimeTimeName = fieldPrefix ? `${fieldPrefix}.endTimeTime` : "endTimeTime";

    const { formState } = useFormContext();
    const { startDate, startTime, endDate, endTime } = getInitialTimes(dateFormat, DEFAULT_TIME_FORMAT);

    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const showArrow = !isXsScreen && !isSmScreen;

    // Get form error if any - access nested error messages at any depth
    const getErrorMessage = (fieldName: string) => {
        const parts = fieldName.split(".");
        let error: any = formState.errors;
        for (const part of parts) {
            error = error?.[part];
            if (!error) break;
        }
        return error?.message;
    };

    const error =
        getErrorMessage(roomStartTimeDateName)
        || getErrorMessage(roomEndTimeDateName)
        || getErrorMessage(roomStartTimeTimeName)
        || getErrorMessage(roomEndTimeTimeName);

    return (
        <>
            <StyledPickersWrapper className={`${baseClassName}`}>
                <PickersUnit
                    dateFieldName={roomStartTimeDateName}
                    timeFieldName={roomStartTimeTimeName}
                    dateDefaultValue={startDate}
                    timeDefaultValue={startTime}
                    label={translate("Start time")}
                    dateFormat={dateFormat}
                    timeFormat={DEFAULT_TIME_FORMAT}
                    disabled={disabled}
                    skipMaxDurationValidation={skipMaxDurationValidation}
                    aria-describedby={ariaDescribedBy}
                />

                {showArrow && <ArrowRight24Icon style={{ marginTop: "27px" }} />}

                <PickersUnit
                    dateFieldName={roomEndTimeDateName}
                    timeFieldName={roomEndTimeTimeName}
                    dateDefaultValue={endDate}
                    timeDefaultValue={endTime}
                    label={translate("End time")}
                    dateFormat={dateFormat}
                    timeFormat={DEFAULT_TIME_FORMAT}
                    disabled={disabled}
                    skipMaxDurationValidation={skipMaxDurationValidation}
                    aria-describedby={ariaDescribedBy}
                />
            </StyledPickersWrapper>

            {error && (
                <StyledTypography
                    variant={"formError"}
                    className={`${baseClassName}-error`}
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </StyledTypography>
            )}
        </>
    );
};

export default PickersSection;
